import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { InsuranceApiService, InsuranceProposalDetail, InsuranceQuoteDetail, InsuranceUwCaseDetail } from './insurance-api.service';
import { InsuranceAiCopilotPanelComponent } from './insurance-ai-copilot-panel.component';
import {
  AI_EXPLANATION_SCALAR_FIELDS,
  BENEFICIARY_ITEM_FIELDS,
  INSURED_PARTY_ITEM_FIELDS,
  RISK_OBJECT_ITEM_FIELDS,
  SELECTED_COVERAGE_ITEM_FIELDS,
  buildPartyItemPayloads,
  buildSnapshotItemPayloads,
  buildUwFormAnswerFields,
  normalizeSelectedCoverageItem,
  parseJsonObject,
  uwDecisionLabelKey,
  uwRoleLabelKey,
  uwRuleActionLabelKey
} from './insurance-snapshot-schemas';

type UwTab = 'risk' | 'rules' | 'forms' | 'decision' | 'audit';

@Component({
  standalone: true,
  selector: 'ain-insurance-uw-case-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    JalaliDateInputComponent,
    InfoHintComponent,
    StructuredJsonDisplayComponent,
    StructuredJsonFormComponent,
    InsuranceAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.uwCaseDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.uw.help.detail.title'"
        [descriptionKey]="'insurance.uw.help.detail.description'"
        [bulletKeys]="[
          'insurance.uw.help.detail.bullets.workflow',
          'insurance.uw.help.detail.bullets.tabs',
          'insurance.uw.help.detail.bullets.decision'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!uwCase()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/uw-cases']">{{ t('catalog.action.backToList') }}</a>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.uw.help.summary.title') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.uw.help.summary.title'"
              [descriptionKey]="'insurance.uw.help.summary.description'"
              [bulletKeys]="[
                'insurance.uw.help.summary.bullets.proposal',
                'insurance.uw.help.summary.bullets.workflow'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.uwInbox.fields.proposalNumber') }}: <strong>{{ uwCase()!.proposalNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(uwCase()!.status)) }}</strong></span>
            <span>{{ t('insurance.uwInbox.fields.workflowInstanceId') }}: <strong>{{ workflowInstanceLabel() }}</strong></span>
            <span>{{ t('insurance.uwInbox.fields.createdAt') }}: <strong>{{ uwCase()!.createdAtUtc | ainDate }}</strong></span>
            <span>{{ t('insurance.uwCaseDetail.fields.riskScore') }}: <strong>{{ uwCase()!.riskScore ?? '-' }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'risk'" (click)="activeTab.set('risk')">{{ t('insurance.uwCaseDetail.tabs.risk') }}</button>
            <button type="button" [class.active]="activeTab() === 'rules'" (click)="activeTab.set('rules')">{{ t('insurance.uwCaseDetail.tabs.rules') }}</button>
            <button type="button" [class.active]="activeTab() === 'forms'" (click)="activeTab.set('forms')">{{ t('insurance.uwCaseDetail.tabs.forms') }}</button>
            <button type="button" [class.active]="activeTab() === 'decision'" (click)="activeTab.set('decision')">{{ t('insurance.uwCaseDetail.tabs.decision') }}</button>
            <button type="button" [class.active]="activeTab() === 'audit'" (click)="activeTab.set('audit')">{{ t('insurance.uwCaseDetail.tabs.audit') }}</button>
          </div>

          @if (activeTab() === 'decision') {
            <div class="lines-header">
              <h4>{{ t('insurance.uwCaseDetail.tabs.decision') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.decision.title'"
                [descriptionKey]="'insurance.uw.help.decision.description'"
                [bulletKeys]="[
                  'insurance.uw.help.decision.bullets.reason',
                  'insurance.uw.help.decision.bullets.refer',
                  'insurance.uw.help.decision.bullets.permissions'
                ]" />
            </div>
            <form class="grid">
              <label class="wide">
                <span>{{ t('insurance.uwCaseDetail.fields.reason') }}</span>
                <textarea pTextarea rows="3" [(ngModel)]="reason" name="reason"></textarea>
              </label>
              <label>
                <span>{{ t('insurance.uwCaseDetail.fields.assignedRole') }}</span>
                <p-select
                  [options]="assignedRoleOptions"
                  optionValue="value"
                  [(ngModel)]="assignedRole"
                  name="assignedRole"
                  [filter]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.uwCaseDetail.fields.dueAtUtc') }}</span>
                <ain-date-input [(ngModel)]="dueAtUtc" name="dueAtUtc" />
              </label>
              <div class="actions wide">
                <button pButton type="button" [label]="t('insurance.uwCaseDetail.actions.approve')" [disabled]="!canApproveAction()" (click)="openApproveDialog()"></button>
                <button pButton type="button" severity="danger" [label]="t('insurance.uwCaseDetail.actions.decline')" [disabled]="!canDeclineAction()" (click)="openDeclineDialog()"></button>
                <button pButton type="button" severity="secondary" [label]="t('insurance.uwCaseDetail.actions.refer')" [disabled]="!canReferAction()" (click)="refer()"></button>
                <button pButton type="button" severity="secondary" [label]="t('insurance.uwCaseDetail.actions.requestInfo')" [disabled]="!canRequestInfoAction()" (click)="requestInfo()"></button>
              </div>
              @if (!canDecide() && !errorMessage()) { <p class="hint wide">{{ t('insurance.uwCaseDetail.messages.caseNotActionable') }}</p> }
              @if (canDecide() && !canAnyDecisionAction() && !errorMessage()) { <p class="hint wide">{{ t('insurance.uwCaseDetail.messages.insufficientPermission') }}</p> }
              @if (successMessage()) { <p class="success wide">{{ successMessage() }}</p> }
              @if (errorMessage()) { <p class="error wide">{{ errorMessage() }}</p> }
            </form>
          } @else if (activeTab() === 'audit') {
            <div class="lines-header">
              <h4>{{ t('insurance.uwCaseDetail.tabs.audit') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.tabs.audit.title'"
                [descriptionKey]="'insurance.uw.help.tabs.audit.description'"
                [bulletKeys]="[
                  'insurance.uw.help.tabs.audit.bullets.timeline',
                  'insurance.uw.help.tabs.audit.bullets.filters'
                ]" />
            </div>
            <div class="section-title subsection">
              <h4>{{ t('insurance.uwCaseDetail.sections.timeline') }}</h4>
            </div>
            <div class="lifecycle-actions">
              <button pButton type="button" size="small" [severity]="auditFilter() === 'all' ? undefined : 'secondary'" [label]="auditFilterLabel('all')" (click)="auditFilter.set('all')"></button>
              <button pButton type="button" size="small" [severity]="auditFilter() === 'rule' ? undefined : 'secondary'" [label]="auditFilterLabel('rule')" (click)="auditFilter.set('rule')"></button>
              <button pButton type="button" size="small" [severity]="auditFilter() === 'decision' ? undefined : 'secondary'" [label]="auditFilterLabel('decision')" (click)="auditFilter.set('decision')"></button>
              <button pButton type="button" size="small" [severity]="auditFilter() === 'referral' ? undefined : 'secondary'" [label]="auditFilterLabel('referral')" (click)="auditFilter.set('referral')"></button>
            </div>
            @if (filteredAuditTimeline().length === 0) {
              <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noTimeline')" />
            } @else {
              <p-table [value]="filteredAuditTimeline()" dataKey="id" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('columns.title') }}</th>
                    <th>{{ t('columns.date') }}</th>
                    <th>{{ t('columns.description') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.title }}</td>
                    <td>{{ row.atUtc | ainDate }}</td>
                    <td>{{ row.detail || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }

            <div class="section-title subsection">
              <h4>{{ t('insurance.uwCaseDetail.sections.decisions') }}</h4>
            </div>
            @if (uwCase()!.decisions.length === 0) {
              <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noDecisions')" />
            } @else {
              <p-table [value]="uwCase()!.decisions" dataKey="id" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.uwCaseDetail.tabs.decision') }}</th>
                    <th>{{ t('columns.date') }}</th>
                    <th>{{ t('insurance.uwCaseDetail.fields.reason') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t(uwDecisionLabelKey(row.decision)) }}</td>
                    <td>{{ row.createdAtUtc | ainDate }}</td>
                    <td>{{ row.overrideReason || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }

            <div class="section-title subsection">
              <h4>{{ t('insurance.uwCaseDetail.sections.referrals') }}</h4>
            </div>
            @if (uwCase()!.referrals.length === 0) {
              <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noReferrals')" />
            } @else {
              <p-table [value]="uwCase()!.referrals" dataKey="id" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.uwCaseDetail.fields.assignedRole') }}</th>
                    <th>{{ t('insurance.uwCaseDetail.fields.dueAtUtc') }}</th>
                    <th>{{ t('columns.description') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t(uwRoleLabelKey(row.assignedToRole)) }}</td>
                    <td>{{ row.dueAtUtc | ainDate }}</td>
                    <td>{{ row.notes || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'risk') {
            <div class="lines-header">
              <h4>{{ t('insurance.uwCaseDetail.tabs.risk') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.tabs.risk.title'"
                [descriptionKey]="'insurance.uw.help.tabs.risk.description'"
                [bulletKeys]="[
                  'insurance.uw.help.tabs.risk.bullets.score',
                  'insurance.uw.help.tabs.risk.bullets.copilot'
                ]" />
            </div>
            <div class="summary-grid">
              <p><strong>{{ t('insurance.uwCaseDetail.fields.riskScore') }}:</strong> {{ uwCase()!.riskScore ?? '-' }}</p>
              <p><strong>{{ t('insurance.uwCaseDetail.fields.reason') }}:</strong> {{ uwCase()!.decisionReason || '-' }}</p>
              <p><strong>{{ t('insurance.uwCaseDetail.fields.proposalStatus') }}:</strong> {{ proposal() ? t(proposalStatusKey(proposal()!.status)) : '-' }}</p>
              <p><strong>{{ t('insurance.uwCaseDetail.fields.totalSumInsured') }}:</strong> {{ uwCase()!.totalSumInsured ?? '-' }}</p>
            </div>
            <div class="actions section-gap">
              <button pButton type="button" size="small" [label]="t('insurance.ai.copilot.generateRiskScore')" [disabled]="generatingRiskScore()" (click)="generateRiskScore()"></button>
            </div>
            @if (riskScoreExplanationJson()) {
              <div class="section-gap">
                <p class="field-label">{{ t('insurance.ai.explanation.summary') }}</p>
                <p class="hint">{{ riskScoreSummary() }}</p>
                <ain-structured-json-display
                  [fields]="explanationScalarFields"
                  [payloadJson]="riskScoreExplanationJson()"
                  [excludeKeys]="['summary']"
                  [emptyMessage]="t('insurance.ai.explanation.noDetails')" />
              </div>
            } @else if (riskScoreSummary()) {
              <p class="hint">{{ riskScoreSummary() }}</p>
            }
          } @else if (activeTab() === 'rules') {
            <div class="lines-header">
              <h4>{{ t('insurance.uwCaseDetail.tabs.rules') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.tabs.rules.title'"
                [descriptionKey]="'insurance.uw.help.tabs.rules.description'"
                [bulletKeys]="[
                  'insurance.uw.help.tabs.rules.bullets.trace',
                  'insurance.uw.help.tabs.rules.bullets.matched'
                ]" />
            </div>
            <p><strong>{{ t('insurance.uwCaseDetail.fields.matchedRule') }}:</strong> {{ matchedRuleLabel() }}</p>
            @if (ruleTraceRows().length === 0) {
              <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noRules')" />
            } @else {
              <p-table [value]="ruleTraceRows()" dataKey="ruleId" [rows]="10" [paginator]="true" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.productVersions.uwRules.priority') }}</th>
                    <th>{{ t('insurance.productVersions.uwRules.action') }}</th>
                    <th>{{ t('insurance.productVersions.uwRules.maxSumInsured') }}</th>
                    <th>{{ t('insurance.uwCaseDetail.fields.ruleReason') }}</th>
                    <th>{{ t('insurance.uwCaseDetail.fields.ruleMatched') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.priority }}</td>
                    <td>{{ t(uwRuleActionLabelKey(row.action)) }}</td>
                    <td>{{ row.maxSumInsured ?? '-' }}</td>
                    <td>{{ resolveRuleReasonLabel(row) }}</td>
                    <td>{{ row.matched ? t('common.yes') : t('common.no') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.uwCaseDetail.sections.formSnapshot') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.tabs.forms.title'"
                [descriptionKey]="'insurance.uw.help.tabs.forms.description'"
                [bulletKeys]="[
                  'insurance.uw.help.tabs.forms.bullets.answers',
                  'insurance.uw.help.tabs.forms.bullets.parties'
                ]" />
            </div>
            <div class="snapshot-block">
              <p class="field-label">{{ t('insurance.uwCaseDetail.fields.uwFormAnswers') }}</p>
              @if (uwFormAnswerFields().length === 0) {
                <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noFormAnswers')" />
              } @else {
                @if (uwFormAnswersQuoteId()) {
                  <label class="quote-link-row">
                    <span>{{ t('insurance.uwCaseDetail.snapshot.quoteReference') }}</span>
                    <button type="button" class="link-btn" (click)="openQuote(uwFormAnswersQuoteId()!)">{{ proposal()?.quoteNumber || uwFormAnswersQuoteId() }}</button>
                  </label>
                }
                <ain-structured-json-form
                  [fields]="uwFormAnswerFieldsWithoutQuote()"
                  [payloadJson]="uwFormAnswersPayloadJson()"
                  [readonly]="true" />
              }
            </div>

            <div class="snapshot-block">
              <p class="field-label">{{ t('insurance.uwCaseDetail.fields.insuredParties') }}</p>
              @if (insuredPartyItems().length === 0) {
                <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noInsuredParties')" />
              } @else {
                @for (item of insuredPartyItems(); track item.index) {
                  <div class="row-card">
                    <ain-structured-json-form
                      [fields]="insuredPartyItemFields"
                      [payloadJson]="item.payloadJson"
                      [readonly]="true" />
                  </div>
                }
              }
            </div>

            <div class="snapshot-block">
              <p class="field-label">{{ t('insurance.uwCaseDetail.fields.beneficiaries') }}</p>
              @if (beneficiaryItems().length === 0) {
                <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noBeneficiaries')" />
              } @else {
                @for (item of beneficiaryItems(); track item.index) {
                  <div class="row-card">
                    <ain-structured-json-form
                      [fields]="beneficiaryItemFields"
                      [payloadJson]="item.payloadJson"
                      [readonly]="true" />
                  </div>
                }
              }
            </div>

            <div class="snapshot-block">
              <p class="field-label">{{ t('insurance.uwCaseDetail.fields.riskObjects') }}</p>
              @if (riskObjectItems().length === 0) {
                <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noRiskObjects')" />
              } @else {
                @for (item of riskObjectItems(); track item.index) {
                  <div class="row-card">
                    <ain-structured-json-form
                      [fields]="riskObjectItemFields"
                      [payloadJson]="item.payloadJson"
                      [readonly]="true" />
                  </div>
                }
              }
            </div>

            <div class="snapshot-block">
              <p class="field-label">{{ t('insurance.uwCaseDetail.fields.selectedCoverages') }}</p>
              @if (selectedCoverageItems().length === 0) {
                <ain-state type="empty" [title]="t('insurance.uwCaseDetail.states.noSelectedCoverages')" />
              } @else {
                @for (item of selectedCoverageItems(); track item.index) {
                  <div class="row-card">
                    <ain-structured-json-form
                      [fields]="selectedCoverageItemFields"
                      [payloadJson]="item.payloadJson"
                      [readonly]="true" />
                  </div>
                }
              }
            </div>
          }
        </section>

        <ain-insurance-ai-copilot-panel entityType="InsuranceUwCase" [entityId]="uwCase()!.id" />

        <p-dialog [header]="t('insurance.uwCaseDetail.actions.approve')" [modal]="true" [(visible)]="approveDialogVisible" [style]="{ width: '480px' }">
          <p>{{ t('insurance.uwCaseDetail.messages.approveConfirm') }}</p>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="approveDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('insurance.uwCaseDetail.actions.approve')" (click)="confirmApprove()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.uwCaseDetail.actions.decline')" [modal]="true" [(visible)]="declineDialogVisible" [style]="{ width: '480px' }">
          <p>{{ t('insurance.uwCaseDetail.messages.declineConfirm') }}</p>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="declineDialogVisible.set(false)"></button>
            <button pButton type="button" severity="danger" [label]="t('insurance.uwCaseDetail.actions.decline')" (click)="confirmDecline()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3, .section-title h4 { margin: 0; }
    .subsection { margin-top: 16px; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 10px; }
    .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .snapshot-block { margin-bottom: 14px; }
    .field-label { margin: 0 0 8px; font-weight: 600; color: #334155; }
    .row-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; margin-bottom: 8px; }
    .quote-link-row { display: grid; gap: 6px; margin-bottom: 10px; }
    .quote-link-row span { color: #475569; font-size: 12px; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-align: start; }
    .hint { color: #6b7280; }
    .success { color: #065f46; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceUwCaseDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly contextService = inject(CurrentContextService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly uwCase = signal<InsuranceUwCaseDetail | null>(null);
  readonly proposal = signal<InsuranceProposalDetail | null>(null);
  readonly quote = signal<InsuranceQuoteDetail | null>(null);
  readonly ruleTraceRows = signal<RuleTraceRow[]>([]);
  readonly uwFormAnswersJson = signal('');
  readonly riskScoreExplanationJson = signal('');
  readonly explanationScalarFields = AI_EXPLANATION_SCALAR_FIELDS;
  readonly auditTimeline = signal<AuditTimelineItem[]>([]);
  readonly auditFilter = signal<AuditTimelineCategory>('all');
  readonly activeTab = signal<UwTab>('risk');
  readonly successMessage = signal('');
  readonly errorMessage = signal('');
  readonly generatingRiskScore = signal(false);
  readonly riskScoreSummary = signal('');
  readonly approveDialogVisible = signal(false);
  readonly declineDialogVisible = signal(false);
  reason = '';
  assignedRole = 'underwriter';
  dueAtUtc = new Date(Date.now() + (2 * 24 * 60 * 60 * 1000)).toISOString();
  readonly assignedRoleOptions = [
    { value: 'underwriter', labelKey: 'insurance.uw.roles.underwriter' },
    { value: 'senior_underwriter', labelKey: 'insurance.uw.roles.seniorUnderwriter' },
    { value: 'underwriting_manager', labelKey: 'insurance.uw.roles.underwritingManager' }
  ];
  readonly insuredPartyItemFields = INSURED_PARTY_ITEM_FIELDS;
  readonly beneficiaryItemFields = BENEFICIARY_ITEM_FIELDS;
  readonly riskObjectItemFields = RISK_OBJECT_ITEM_FIELDS;
  readonly selectedCoverageItemFields = SELECTED_COVERAGE_ITEM_FIELDS;

  readonly uwFormAnswersPayloadJson = computed(() => {
    const raw = this.uwFormAnswersJson().trim();
    if (!raw) {
      return '{}';
    }
    try {
      return JSON.stringify(parseJsonObject(raw));
    } catch {
      return '{}';
    }
  });

  readonly uwFormAnswerFields = computed(() => buildUwFormAnswerFields(this.uwFormAnswersPayloadJson()));

  readonly uwFormAnswerFieldsWithoutQuote = computed(() =>
    this.uwFormAnswerFields().filter(field => field.key !== 'quoteId')
  );

  readonly uwFormAnswersQuoteId = computed(() => {
    const quoteId = parseJsonObject(this.uwFormAnswersPayloadJson())['quoteId'];
    return typeof quoteId === 'string' && quoteId.trim() ? quoteId.trim() : null;
  });

  readonly insuredPartyItems = computed(() => buildPartyItemPayloads(this.proposal()?.insuredPartiesJson));

  readonly beneficiaryItems = computed(() => buildPartyItemPayloads(this.proposal()?.beneficiariesJson));

  readonly riskObjectItems = computed(() => buildSnapshotItemPayloads(this.quote()?.riskObjectsJson));

  readonly selectedCoverageItems = computed(() =>
    buildSnapshotItemPayloads(this.quote()?.selectedCoveragesJson, normalizeSelectedCoverageItem)
  );

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('uwCaseId');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.api.getUwCase(id).subscribe({
      next: item => {
        this.uwCase.set(item);
        if (item) {
          this.parseRuleTrace(item.ruleEvaluationTraceJson);
          this.loadContext(item.proposalId);
        }
        this.loading.set(false);
      },
      error: () => {
        this.uwCase.set(null);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    const current = this.uwCase();
    if (!current) {
      return this.t('insurance.uwCaseDetail.title');
    }

    return `${this.t('insurance.uwCaseDetail.title')} — ${current.proposalNumber}`;
  }

  openApproveDialog(): void {
    this.approveDialogVisible.set(true);
  }

  openDeclineDialog(): void {
    this.declineDialogVisible.set(true);
  }

  confirmApprove(): void {
    this.approveDialogVisible.set(false);
    this.approve();
  }

  confirmDecline(): void {
    this.declineDialogVisible.set(false);
    this.decline();
  }

  approve(): void {
    const current = this.uwCase();
    if (!current) {
      return;
    }
    if (!this.hasUwAction('Approve')) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.insufficientPermission'));
      return;
    }
    if (!this.reason.trim()) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.reasonRequired'));
      return;
    }

    this.api.approveUwCase(current.id, {
      overrideReason: this.reason.trim(),
      aiRecommendationJson: null
    }).subscribe({
      next: item => {
        this.applyUpdatedCase(item);
        this.successMessage.set(this.t('insurance.uwCaseDetail.messages.approved'));
        this.errorMessage.set('');
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  decline(): void {
    const current = this.uwCase();
    if (!current) {
      return;
    }
    if (!this.hasUwAction('Reject')) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.insufficientPermission'));
      return;
    }
    if (!this.reason.trim()) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.reasonRequired'));
      return;
    }

    this.api.declineUwCase(current.id, {
      decisionReason: this.reason.trim(),
      overrideReason: this.reason.trim(),
      aiRecommendationJson: null
    }).subscribe({
      next: item => {
        this.applyUpdatedCase(item);
        this.successMessage.set(this.t('insurance.uwCaseDetail.messages.declined'));
        this.errorMessage.set('');
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  refer(): void {
    const current = this.uwCase();
    if (!current) {
      return;
    }
    if (!this.hasUwAction('Assign')) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.insufficientPermission'));
      return;
    }

    this.api.referUwCase(current.id, {
      assignedToRole: this.assignedRole || 'underwriter',
      dueAtUtc: this.dueAtUtc,
      notes: this.reason || null,
      workflowInstanceId: null
    }).subscribe({
      next: item => {
        this.applyUpdatedCase(item);
        this.successMessage.set(this.t('insurance.uwCaseDetail.messages.referred'));
        this.errorMessage.set('');
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  requestInfo(): void {
    const current = this.uwCase();
    if (!current) {
      return;
    }
    if (!this.hasUwAction('Request')) {
      this.errorMessage.set(this.t('insurance.uwCaseDetail.messages.insufficientPermission'));
      return;
    }

    this.api.requestUwCaseInfo(current.id, { notes: this.reason || 'additional_info_required' }).subscribe({
      next: item => {
        this.applyUpdatedCase(item);
        this.successMessage.set(this.t('insurance.uwCaseDetail.messages.infoRequested'));
        this.errorMessage.set('');
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  canDecide(): boolean {
    const status = this.uwCase()?.status;
    return status === 'Open' || status === 'Referred' || status === 'PendingInfo';
  }

  canReassign(): boolean {
    return this.canDecide();
  }

  canApproveAction(): boolean {
    return this.canDecide() && this.hasUwAction('Approve') && this.reason.trim().length > 0;
  }

  canDeclineAction(): boolean {
    return this.canDecide() && this.hasUwAction('Reject') && this.reason.trim().length > 0;
  }

  canReferAction(): boolean {
    return this.canReassign() && this.hasUwAction('Assign');
  }

  canRequestInfoAction(): boolean {
    return this.canReassign() && this.hasUwAction('Request');
  }

  canAnyDecisionAction(): boolean {
    return this.canApproveAction() || this.canDeclineAction() || this.canReferAction() || this.canRequestInfoAction();
  }

  statusKey(status: string): string {
    return `insurance.uw.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  proposalStatusKey(status: string): string {
    return `insurance.proposals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  workflowInstanceLabel(): string {
    return this.uwCase()?.workflowInstanceId
      ? this.t('insurance.uwInbox.fields.workflowLinked')
      : this.t('insurance.uwInbox.fields.workflowNotLinked');
  }

  openQuote(quoteId: string): void {
    void this.router.navigate(['/insurance/quotes', quoteId]);
  }

  uwRuleActionLabelKey(action: string): string {
    return uwRuleActionLabelKey(action);
  }

  uwDecisionLabelKey(decision: string): string {
    return uwDecisionLabelKey(decision);
  }

  uwRoleLabelKey(role: string): string {
    return uwRoleLabelKey(role);
  }

  matchedRuleLabel(): string {
    const id = this.uwCase()?.matchedUwRuleId;
    if (!id) {
      return this.t('insurance.uwCaseDetail.fields.noRuleMatched');
    }

    const match = this.ruleTraceRows().find(item => item.ruleId === id);
    if (!match) {
      return this.t('insurance.uwCaseDetail.fields.noRuleMatched');
    }

    return `P${match.priority} / ${this.t(this.uwRuleActionLabelKey(match.action))}`;
  }

  private loadContext(proposalId: string): void {
    this.api.getProposal(proposalId).subscribe({
      next: proposal => {
        this.proposal.set(proposal);
        if (!proposal) return;
        this.api.getQuote(proposal.quoteId).subscribe({
          next: quote => {
            this.quote.set(quote);
          },
          error: () => {
            this.quote.set(null);
          }
        });
      },
      error: () => {
        this.proposal.set(null);
      }
    });
  }

  private applyUpdatedCase(item: InsuranceUwCaseDetail): void {
    this.uwCase.set(item);
    this.parseRuleTrace(item.ruleEvaluationTraceJson);
    this.auditFilter.set('all');
  }

  private parseRuleTrace(raw: string): void {
    try {
      const parsed = JSON.parse(raw) as { evaluatedRules?: RuleTraceRow[]; uwFormAnswersJson?: string | null };
      this.ruleTraceRows.set(Array.isArray(parsed.evaluatedRules) ? parsed.evaluatedRules : []);
      this.uwFormAnswersJson.set(parsed.uwFormAnswersJson?.trim() ? parsed.uwFormAnswersJson : '');
      this.rebuildAuditTimeline();
    } catch {
      this.ruleTraceRows.set([]);
      this.uwFormAnswersJson.set('');
      this.rebuildAuditTimeline();
    }
  }

  private rebuildAuditTimeline(): void {
    const current = this.uwCase();
    if (!current) {
      this.auditTimeline.set([]);
      return;
    }

    const items: AuditTimelineItem[] = [];
    items.push({
      id: `case-${current.id}`,
      atUtc: current.createdAtUtc,
      title: this.t('insurance.uwCaseDetail.timeline.caseCreated'),
      detail: this.t(this.statusKey(current.status)),
      category: 'decision'
    });

    for (const row of this.ruleTraceRows()) {
      const matched = !!row.matched;
      items.push({
        id: `rule-${row.ruleId}-${row.priority}`,
        atUtc: current.createdAtUtc,
        title: matched
          ? this.t('insurance.uwCaseDetail.timeline.ruleMatched')
          : this.t('insurance.uwCaseDetail.timeline.ruleSkipped'),
        detail: `P${row.priority} / ${this.t(this.uwRuleActionLabelKey(row.action))} / ${this.resolveRuleReasonLabel(row)}`,
        category: 'rule'
      });
    }

    for (const decision of current.decisions) {
      items.push({
        id: `decision-${decision.id}`,
        atUtc: decision.createdAtUtc,
        title: this.t('insurance.uwCaseDetail.timeline.decision'),
        detail: `${this.t(this.uwDecisionLabelKey(decision.decision))}${decision.overrideReason ? ` / ${decision.overrideReason}` : ''}`,
        category: 'decision'
      });
    }

    for (const referral of current.referrals) {
      items.push({
        id: `referral-${referral.id}`,
        atUtc: referral.dueAtUtc,
        title: this.t('insurance.uwCaseDetail.timeline.referral'),
        detail: `${this.t(this.uwRoleLabelKey(referral.assignedToRole))}${referral.notes ? ` / ${referral.notes}` : ''}`,
        category: 'referral'
      });
    }

    items.sort((a, b) => new Date(b.atUtc).getTime() - new Date(a.atUtc).getTime());
    this.auditTimeline.set(items);
  }

  filteredAuditTimeline(): AuditTimelineItem[] {
    const filter = this.auditFilter();
    if (filter === 'all') {
      return this.auditTimeline();
    }

    return this.auditTimeline().filter(item => item.category === filter);
  }

  auditFilterLabel(category: AuditTimelineCategory): string {
    const key = category === 'all'
      ? 'insurance.uwCaseDetail.filters.all'
      : `insurance.uwCaseDetail.filters.${category}`;
    return `${this.t(key)} (${this.auditCount(category)})`;
  }

  private auditCount(category: AuditTimelineCategory): number {
    if (category === 'all') {
      return this.auditTimeline().length;
    }

    return this.auditTimeline().filter(item => item.category === category).length;
  }

  resolveRuleReasonLabel(row: RuleTraceRow): string {
    if (row.evaluationReason === 'matched_all_criteria') {
      return this.t('insurance.uwCaseDetail.fields.ruleReason.matched');
    }

    if (row.evaluationReason === 'skipped_total_sum_insured_within_max_threshold') {
      return this.t('insurance.uwCaseDetail.fields.ruleReason.skippedByThreshold');
    }

    if (row.evaluationReason === 'skipped_zone_code_not_matched') {
      return this.t('insurance.uwCaseDetail.fields.ruleReason.skippedByZone');
    }

    if (row.evaluationReason === 'skipped_condition_not_matched') {
      return this.t('insurance.uwCaseDetail.fields.ruleReason.skippedByCondition');
    }

    return '-';
  }

  private hasUwAction(requiredAction: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const uwPermission = (context?.permissions ?? []).find(item => item.key === 'insurance.uw');
    if (!uwPermission) {
      return false;
    }

    const actions = new Set((uwPermission.actions ?? []).map(action => action.toLowerCase()));
    return actions.has(requiredAction.toLowerCase());
  }

  generateRiskScore(): void {
    const uwCaseId = this.uwCase()?.id;
    if (!uwCaseId) return;
    this.generatingRiskScore.set(true);
    this.api.generateUwRiskScore(uwCaseId, true).subscribe({
      next: row => {
        this.riskScoreExplanationJson.set(row.explanationJson || '{}');
        try {
          const parsed = JSON.parse(row.explanationJson) as { summary?: string };
          this.riskScoreSummary.set(parsed.summary || this.t(this.recommendationTypeLabelKey(row.recommendationType)));
        } catch {
          this.riskScoreSummary.set(this.t(this.recommendationTypeLabelKey(row.recommendationType)));
        }
        this.generatingRiskScore.set(false);
      },
      error: () => this.generatingRiskScore.set(false)
    });
  }

  recommendationTypeLabelKey(recommendationType: string): string {
    return `insurance.ai.recommendationTypes.${recommendationType.charAt(0).toLowerCase()}${recommendationType.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}

type RuleTraceRow = {
  ruleId: string;
  priority: number;
  action: string;
  maxSumInsured: number | null;
  matched: boolean;
  evaluationReason?: string | null;
};

type AuditTimelineItem = {
  id: string;
  atUtc: string;
  title: string;
  detail: string;
  category: AuditTimelineCategory;
};

type AuditTimelineCategory = 'all' | 'rule' | 'decision' | 'referral';
